/**
 * Aggregates org-facing catalogue commerce signals:
 * - **Draft / live**: visitor shelf state (not yet submitted to the studio).
 * - **Submitted / final**: wishlist + cart-intent submissions from account submit or legacy flows.
 */

export type ProductCommerceSignal = {
  /** Finalized: distinct shortlist submissions that included this product */
  shortlistSubmissions: number;
  /** Finalized: total shortlist line-items for this product */
  shortlistLines: number;
  /** Finalized: distinct cart-intent submissions that included this product */
  cartSubmissions: number;
  /** Finalized: sum of quantities across cart lines */
  cartUnits: number;
  /** Finalized: latest ISO timestamp from either submission stream */
  lastActivityIso: string | null;
  /** Best-effort label from submission or shelf lines */
  productName: string | null;

  /** Live draft: distinct visitors with this product on shelf shortlist */
  draftShortlistVisitors: number;
  /** Live draft: distinct visitors with this product on shelf cart */
  draftCartVisitors: number;
  /** Live draft: sum of shelf cart quantities for this product */
  draftCartUnits: number;
  /** Live draft: latest shelf update time touching this product */
  draftLastActivityIso: string | null;
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token || ""}`,
  };
}

export function lineProductKey(line: any): string {
  return String(line?.productUniqueId || line?.productId || "").trim();
}

/** Shelf / visitor line id (falls back to productKey). */
export function shelfLineProductKey(line: any): string {
  return String(
    line?.productUniqueId || line?.productId || line?.productKey || ""
  ).trim();
}

function ensure(
  acc: Record<string, ProductCommerceSignal>,
  key: string
): ProductCommerceSignal {
  if (!acc[key]) {
    acc[key] = {
      shortlistSubmissions: 0,
      shortlistLines: 0,
      cartSubmissions: 0,
      cartUnits: 0,
      lastActivityIso: null,
      productName: null,
      draftShortlistVisitors: 0,
      draftCartVisitors: 0,
      draftCartUnits: 0,
      draftLastActivityIso: null,
    };
  }
  return acc[key];
}

function maxIso(current: string | null, next: unknown): string | null {
  if (next == null) return current;
  const s =
    typeof next === "string"
      ? next
      : next instanceof Date
        ? next.toISOString()
        : String(next);
  if (!s) return current;
  if (!current) return s;
  const a = Date.parse(current);
  const b = Date.parse(s);
  if (Number.isNaN(a)) return s;
  if (Number.isNaN(b)) return current;
  return b >= a ? s : current;
}

/**
 * Build per–product-unique-id rollup from raw API lists (finalized submissions only).
 */
export function aggregateCommerceSignals(
  wishlists: any[],
  carts: any[]
): Record<string, ProductCommerceSignal> {
  const acc: Record<string, ProductCommerceSignal> = {};

  for (const sub of wishlists || []) {
    const lines = sub?.wishlistItems || [];
    const perKeyLines = new Map<string, number>();
    const perKeyName = new Map<string, string>();
    for (const line of lines) {
      const k = lineProductKey(line);
      if (!k) continue;
      perKeyLines.set(k, (perKeyLines.get(k) || 0) + 1);
      if (line?.productName && !perKeyName.has(k)) {
        perKeyName.set(k, String(line.productName));
      }
    }
    const t = sub?.submissionDate ?? sub?.lastUpdated;
    for (const [k, lineCount] of Array.from(perKeyLines.entries())) {
      const row = ensure(acc, k);
      row.shortlistSubmissions += 1;
      row.shortlistLines += lineCount;
      row.productName = row.productName || perKeyName.get(k) || null;
      row.lastActivityIso = maxIso(row.lastActivityIso, t);
    }
  }

  for (const sub of carts || []) {
    const lines = sub?.cartItems || [];
    const perKey = new Map<string, { units: number; name: string | null }>();
    for (const line of lines) {
      const k = lineProductKey(line);
      if (!k) continue;
      const q = Math.max(1, Math.trunc(Number(line?.quantity) || 1));
      const prev = perKey.get(k) || { units: 0, name: null };
      prev.units += q;
      if (line?.productName && !prev.name) prev.name = String(line.productName);
      perKey.set(k, prev);
    }
    const t = sub?.submissionDate ?? sub?.lastUpdated;
    for (const [k, { units, name }] of Array.from(perKey.entries())) {
      const row = ensure(acc, k);
      row.cartSubmissions += 1;
      row.cartUnits += units;
      row.productName = row.productName || name;
      row.lastActivityIso = maxIso(row.lastActivityIso, t);
    }
  }

  return acc;
}

/**
 * Merge live visitor shelf drafts into the rollup (one shelf document ≈ one visitor per catalogue).
 */
export function mergeDraftShelfSignals(
  acc: Record<string, ProductCommerceSignal>,
  shelves: any[]
): Record<string, ProductCommerceSignal> {
  for (const shelf of shelves || []) {
    const t = shelf?.updatedAt ?? shelf?.updated_at;
    const shorts: any[] = Array.isArray(shelf?.shortlistLines) ? shelf.shortlistLines : [];
    const carts: any[] = Array.isArray(shelf?.cartLines) ? shelf.cartLines : [];

    const shortKeys = new Set<string>();
    for (const line of shorts) {
      const k = shelfLineProductKey(line);
      if (!k) continue;
      shortKeys.add(k);
      const row = ensure(acc, k);
      if (line?.productName && !row.productName) {
        row.productName = String(line.productName);
      }
    }
    for (const k of Array.from(shortKeys)) {
      const row = ensure(acc, k);
      row.draftShortlistVisitors += 1;
      row.draftLastActivityIso = maxIso(row.draftLastActivityIso, t);
    }

    const cartUnitsByKey = new Map<string, number>();
    const cartNameByKey = new Map<string, string>();
    for (const line of carts) {
      const k = shelfLineProductKey(line);
      if (!k) continue;
      const q = Math.max(1, Math.trunc(Number(line?.quantity) || 1));
      cartUnitsByKey.set(k, (cartUnitsByKey.get(k) || 0) + q);
      if (line?.productName && !cartNameByKey.has(k)) {
        cartNameByKey.set(k, String(line.productName));
      }
    }
    for (const [k, units] of Array.from(cartUnitsByKey.entries())) {
      const row = ensure(acc, k);
      row.draftCartVisitors += 1;
      row.draftCartUnits += units;
      row.productName = row.productName || cartNameByKey.get(k) || null;
      row.draftLastActivityIso = maxIso(row.draftLastActivityIso, t);
    }
  }
  return acc;
}

export async function fetchWishlistsForCatalogue(
  catalogueId: string
): Promise<any[]> {
  const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
  if (!base || !catalogueId) return [];
  const res = await fetch(
    `${base}/catalogue-wishlist/catalogue/${encodeURIComponent(catalogueId)}`,
    { headers: authHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) return [];
  return Array.isArray(json.data) ? json.data : [];
}

export async function fetchCartSubmissionsForCatalogue(
  catalogueId: string
): Promise<any[]> {
  const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
  if (!base || !catalogueId) return [];
  const res = await fetch(
    `${base}/catalogue-cart/catalogue/${encodeURIComponent(catalogueId)}`,
    { headers: authHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) return [];
  return Array.isArray(json.data) ? json.data : [];
}

/** Org-only: visitor shelf documents with remaining lines (draft state). */
export async function fetchDraftShelvesForCatalogue(
  catalogueId: string
): Promise<any[]> {
  const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
  if (!base || !catalogueId) return [];
  const res = await fetch(
    `${base}/catalogue-visitor/org/catalogue/${encodeURIComponent(catalogueId)}/draft-shelves`,
    { headers: authHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) return [];
  return Array.isArray(json.data) ? json.data : [];
}

export async function loadCatalogueCommerceRollup(catalogueId: string): Promise<{
  wishlists: any[];
  carts: any[];
  draftShelves: any[];
  byProductId: Record<string, ProductCommerceSignal>;
}> {
  const [wishlists, carts, draftShelves] = await Promise.all([
    fetchWishlistsForCatalogue(catalogueId),
    fetchCartSubmissionsForCatalogue(catalogueId),
    fetchDraftShelvesForCatalogue(catalogueId),
  ]);
  let byProductId = aggregateCommerceSignals(wishlists, carts);
  byProductId = mergeDraftShelfSignals(byProductId, draftShelves);
  return {
    wishlists,
    carts,
    draftShelves,
    byProductId,
  };
}

/** Match a catalogue row id (productUniqueId or mongo id) to rollup keys. */
export function signalForProductRow(
  byProductId: Record<string, ProductCommerceSignal>,
  rowId: string
): ProductCommerceSignal | null {
  const k = String(rowId || "").trim();
  if (!k) return null;
  if (byProductId[k]) return byProductId[k];
  return null;
}
